/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: English
 *
 * ## Style Direction (from analysis)
 * <style-name>BlueprintWire</style-name>
 * **Mode**: Light
 * **Style Recipe**: Outlined / Wireframe + Bento Box / Modular + High-Density
 * **Implementation Techniques**: Rejects shadows entirely to create a lightweight, precision-engineering aesthetic ideal for power-user tools. Every container, widget, and input is defined by crisp 1px solid borders. Corners are sharp or very slightly rounded (4px). The layout packs information tightly into a modular bento grid, making it highly scannable. Typography mixes a highly legible UI sans-serif (like Inter) with a prominent monospaced font (like JetBrains Mono) for Token names, code snippets, and metadata, reinforcing the technical nature of the product.
 * **Color Direction**: Pure crisp white base (`#FFFFFF`). Borders are a subtle cool gray (`#E2E8F0`). The palette is monochromatic and restrained to let the actual components stand out. Accent color is a deep, sophisticated Forest Green or Cobalt Blue, used extremely sparingly (only for active states, focus rings, and primary action buttons).
 * ============================================================
 */

import React, { useState } from 'react';
import { Icon } from '@iconify/react';

const MOCK_STATS = [
  { label: 'Total Components', value: '142', trend: '+12% this month' },
  { label: 'In Draft', value: '12', trend: '3 needs review' },
  { label: 'In Review', value: '5', trend: 'Avg. 2 days' },
  { label: 'Published', value: '125', trend: 'All systems go' },
];

const MOCK_UPDATES = [
  { name: 'Button', version: 'v1.2.0', status: 'Published', user: 'JD', time: '2h ago' },
  { name: 'Checkbox', version: 'v0.9.0', status: 'Draft', user: 'AM', time: '5h ago' },
  { name: 'Dropdown', version: 'v2.0.1', status: 'Review', user: 'SR', time: '1d ago' },
  { name: 'Modal', version: 'v1.1.4', status: 'Published', user: 'JD', time: '2d ago' },
  { name: 'Toast', version: 'v1.0.0', status: 'Published', user: 'EK', time: '3d ago' },
];

const MOCK_ACTIVITY = [
  { user: 'Sarah', action: 'published v1.2.0 of', target: 'Button', time: '2h ago' },
  { user: 'Mike', action: 'created draft', target: 'Checkbox', time: '5h ago' },
  { user: 'Emma', action: 'updated tokens in', target: 'Colors', time: '1d ago' },
];

export default function DashboardBlueprintWire() {
  const [activeNav, setActiveNav] = useState('dashboard');
  const [searchFocused, setSearchFocused] = useState(false);

  return (
    <div className="flex h-screen w-full bg-white text-slate-900 font-sans selection:bg-blue-100 selection:text-blue-900 overflow-hidden">
      
      {/* Sidebar - Outlined / Wireframe */}
      <aside className="w-64 h-full border-r border-slate-200 bg-white flex flex-col shrink-0 relative z-10">
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <Icon icon="lucide:layers" className="w-5 h-5 text-blue-600 mr-2" />
          <span className="font-semibold text-sm tracking-tight">ComponentHub</span>
        </div>

        <nav className="flex-1 overflow-y-auto py-6 px-4 space-y-1">
          <div className="text-xs font-mono text-slate-400 mb-3 px-2 uppercase tracking-wider">Workspace</div>
          
          <button
            onClick={() => setActiveNav('dashboard')}
            className={`w-full flex items-center px-3 py-2 text-sm border ${
              activeNav === 'dashboard' 
                ? 'border-blue-600 bg-blue-50 text-blue-700' 
                : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50'
            } transition-colors rounded-none`}
          >
            <Icon icon="lucide:layout-dashboard" className="w-4 h-4 mr-3 shrink-0" />
            Dashboard
          </button>
          
          <button
            onClick={() => setActiveNav('components')}
            className={`w-full flex items-center px-3 py-2 text-sm border ${
              activeNav === 'components' 
                ? 'border-blue-600 bg-blue-50 text-blue-700' 
                : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50'
            } transition-colors rounded-none`}
          >
            <Icon icon="lucide:box" className="w-4 h-4 mr-3 shrink-0" />
            Components
          </button>

          <button
            onClick={() => setActiveNav('tokens')}
            className={`w-full flex items-center px-3 py-2 text-sm border ${
              activeNav === 'tokens' 
                ? 'border-blue-600 bg-blue-50 text-blue-700' 
                : 'border-transparent text-slate-600 hover:border-slate-200 hover:bg-slate-50'
            } transition-colors rounded-none`}
          >
            <Icon icon="lucide:palette" className="w-4 h-4 mr-3 shrink-0" />
            Design Tokens
          </button>
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center px-2 py-2 border border-slate-200 bg-slate-50">
            <div className="w-6 h-6 bg-slate-200 rounded-sm flex items-center justify-center text-xs font-mono font-medium text-slate-600 mr-2">
              JD
            </div>
            <div className="text-xs truncate text-slate-600">john.doe@example.com</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50/50">
        
        {/* Header */}
        <header className="h-16 flex items-center justify-between px-8 border-b border-slate-200 bg-white shrink-0 relative z-10">
          <h1 className="text-lg font-medium">Dashboard</h1>
          
          <div className="flex items-center space-x-6">
            <div className={`relative flex items-center w-64 border transition-colors ${searchFocused ? 'border-blue-600 ring-1 ring-blue-600/20' : 'border-slate-300'}`}>
              <Icon icon="lucide:search" className="w-4 h-4 text-slate-400 absolute left-3" />
              <input 
                type="text" 
                placeholder="Search components..." 
                className="w-full pl-9 pr-3 py-1.5 text-sm bg-transparent outline-none placeholder:text-slate-400 font-mono"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
              <div className="absolute right-2 flex items-center space-x-1">
                <kbd className="text-[10px] font-mono border border-slate-200 px-1.5 text-slate-400 bg-slate-50">⌘</kbd>
                <kbd className="text-[10px] font-mono border border-slate-200 px-1.5 text-slate-400 bg-slate-50">K</kbd>
              </div>
            </div>
            
            <button className="relative p-2 text-slate-400 hover:text-slate-600 border border-transparent hover:border-slate-200 transition-colors">
              <Icon icon="lucide:bell" className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-blue-600 rounded-none"></span>
            </button>
          </div>
        </header>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Stats Grid - Bento Box */}
            <div className="grid grid-cols-4 gap-4">
              {MOCK_STATS.map((stat, idx) => (
                <div key={idx} className="bg-white border border-slate-200 p-5 flex flex-col justify-between">
                  <div className="text-sm font-mono text-slate-500 mb-2">{stat.label}</div>
                  <div className="text-3xl font-light tracking-tight mb-2">{stat.value}</div>
                  <div className="text-xs text-slate-400 flex items-center">
                    {idx === 0 ? <Icon icon="lucide:trending-up" className="w-3 h-3 mr-1 text-blue-600" /> : null}
                    {stat.trend}
                  </div>
                </div>
              ))}
            </div>

            {/* Main Area */}
            <div className="grid grid-cols-3 gap-6">
              
              {/* Recent Updates */}
              <div className="col-span-2 space-y-4">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <h2 className="text-sm font-semibold uppercase tracking-wider">Recent Updates</h2>
                  <button className="text-xs font-mono text-blue-600 hover:text-blue-800 flex items-center">
                    View All <Icon icon="lucide:arrow-right" className="w-3 h-3 ml-1" />
                  </button>
                </div>
                
                <div className="bg-white border border-slate-200 divide-y divide-slate-100">
                  {MOCK_UPDATES.map((update, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer group">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 group-hover:border-blue-200 group-hover:text-blue-500 transition-colors">
                          <Icon icon="lucide:component" className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-900">{update.name}</div>
                          <div className="text-xs font-mono text-slate-500 mt-0.5">{update.version} • {update.time}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`text-[10px] font-mono px-2 py-0.5 border ${
                          update.status === 'Published' ? 'border-green-200 bg-green-50 text-green-700' :
                          update.status === 'Draft' ? 'border-slate-200 bg-slate-50 text-slate-600' :
                          'border-amber-200 bg-amber-50 text-amber-700'
                        }`}>
                          {update.status}
                        </span>
                        <div className="w-6 h-6 border border-slate-200 bg-white flex items-center justify-center text-[10px] font-mono text-slate-600">
                          {update.user}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar Actions & Activity */}
              <div className="col-span-1 space-y-6">
                
                {/* Actions */}
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white border border-blue-700 px-4 py-2.5 hover:bg-blue-700 transition-colors text-sm font-medium shadow-[2px_2px_0px_rgba(30,58,138,0.2)] active:shadow-none active:translate-y-[2px] active:translate-x-[2px]">
                    <Icon icon="lucide:plus" className="w-4 h-4" />
                    <span>New Component</span>
                  </button>
                  <button className="w-full flex items-center justify-center space-x-2 bg-white text-slate-700 border border-slate-300 px-4 py-2.5 hover:bg-slate-50 hover:border-slate-400 transition-colors text-sm font-medium">
                    <Icon icon="lucide:settings-2" className="w-4 h-4" />
                    <span>Manage Tokens</span>
                  </button>
                </div>

                {/* Activity */}
                <div className="space-y-4 pt-4">
                  <div className="border-b border-slate-200 pb-2">
                    <h2 className="text-sm font-semibold uppercase tracking-wider">Team Activity</h2>
                  </div>
                  
                  <div className="relative border-l border-slate-200 ml-3 space-y-6 py-2">
                    {MOCK_ACTIVITY.map((activity, idx) => (
                      <div key={idx} className="relative pl-6">
                        <span className="absolute -left-[5px] top-1.5 w-2 h-2 bg-white border border-slate-400 rounded-none"></span>
                        <div className="text-sm">
                          <span className="font-medium">{activity.user}</span>{' '}
                          <span className="text-slate-500">{activity.action}</span>{' '}
                          <span className="font-mono text-xs border border-slate-200 px-1 bg-white">{activity.target}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1 font-mono">{activity.time}</div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
